import React from "react";
import styled from "styled-components";
import {
  ArrowDownRight01Icon,
  ArrowUpRight01Icon,
} from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line, radii, space } from "src/theme/ui";

/**
 * The pieces a dialog's body is built from.
 *
 * These existed twice, differently. Adjust Triggers had its own footer row
 * wrapping the app's older button — an outlined blue Cancel beside a Save
 * carrying a check icon — while Top Up had a second footer row wrapping bare
 * HTML buttons styled inline. Two dialogs a click apart in the same table,
 * looking like they came from different products.
 *
 * One set, so a panel is a panel and an action row is an action row wherever
 * it appears.
 */

/**
 * The row of actions at the end of a dialog.
 *
 * Right-aligned, because that is where this product puts the thing you press
 * to finish. It stacks on a narrow screen, where two side by side would each
 * be too small to read a label in — reversed, so the confirming action stays
 * nearest the thumb.
 */
export const DialogActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${space.sm}px;

  @media (max-width: 480px) {
    flex-direction: column-reverse;
    align-items: stretch;

    > * {
      width: 100%;
    }
  }
`;

/**
 * A section surface inside a dialog.
 *
 * A step darker than the card rather than a shade off it: at the tint these
 * started with, a panel and the dialog behind it were within one step of each
 * other and the body read as a single undifferentiated sheet.
 */
export const DialogPanel = styled.div<{ $tone?: "neutral" | "accent" }>`
  border-radius: ${radii.control}px;
  border: 1px solid
    ${({ $tone }) => ($tone === "accent" ? colors.primary[15] : line.hairline)};
  background: ${({ $tone }) =>
    $tone === "accent" ? colors.primary[10] : colors.gray[50]};
`;

/** Figures laid side by side, divided rather than spaced. */
export const DialogStats = styled(DialogPanel)`
  display: grid;
  grid-template-columns: repeat(
    ${({ children }) => React.Children.count(children)},
    1fr
  );
  overflow: hidden;

  > * + * {
    border-left: 1px solid ${line.hairline};
  }

  /**
   * Two up on a phone, not one.
   *
   * One column turned four readings into four full-width tiles — a 16px
   * figure on its own line, four times — and on a 358px dialog that came to
   * about 520px of the window before the reader reached the first field.
   * Two columns is 88px for the same four figures, and it keeps them reading
   * as a strip of stats rather than as a list that happens to be numbers.
   *
   * The dividers follow the grid: every cell takes a top rule except the
   * first row, and every cell in the right-hand column takes a left rule.
   * Keyed on the row position rather than on adjacency, because a single
   * sibling selector cannot know which cells begin a line.
   */
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > * + * {
      border-left: 0;
    }

    > *:nth-child(2n) {
      border-left: 1px solid ${line.hairline};
    }

    > *:nth-child(n + 3) {
      border-top: 1px solid ${line.hairline};
    }
  }
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  padding: 12px 14px;
`;

const StatLabel = styled.span`
  font-size: 11.5px;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: ${colors.gray[600]};
`;

const StatValue = styled.span<{ $tone?: StatTone }>`
  display: flex;
  align-items: center;
  gap: 4px;
  /* Same rule as a row's figure: never broken across lines. */
  white-space: nowrap;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.014em;
  font-variant-numeric: tabular-nums;
  color: ${({ $tone }) =>
    $tone === "gain"
      ? colors.success[600]
      : $tone === "loss"
      ? colors.error[600]
      : colors.gray[900]};
`;

export type StatTone = "gain" | "loss";

export interface DialogStatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /**
   * Colours the figure. A gain or a loss printed in the accent reads as a
   * link.
   */
  tone?: StatTone;
  /**
   * Shows the direction arrow.
   *
   * Off by default, because in this product the arrow belongs to a RETURN and
   * not to an amount: the tables mark the percentage with it and leave the
   * money to its colour and its sign. A figure that carries both an arrow and
   * a minus sign says the same thing twice.
   */
  arrow?: boolean;
}

export const DialogStat: React.FC<DialogStatProps> = ({
  label,
  value,
  tone,
  arrow,
}) => (
  <StatCell>
    <StatLabel>{label}</StatLabel>
    <StatValue $tone={tone}>
      {arrow && tone && (
        <HugeIcon
          icon={tone === "loss" ? ArrowDownRight01Icon : ArrowUpRight01Icon}
          size={15}
        />
      )}
      {value}
    </StatValue>
  </StatCell>
);

/** A line inside a panel: what it is on the left, what it is worth on the right. */
const RowLine = styled.div<{ $total?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
  padding: 8px 0;
  font-size: 12.5px;
  color: ${({ $total }) => ($total ? colors.gray[800] : colors.gray[600])};
  font-weight: ${({ $total }) => ($total ? 500 : 400)};

  /* A total is ruled off from what makes it up. */
  ${({ $total }) => ($total ? `border-top: 1px solid ${line.hairline};` : "")}
`;

const RowValue = styled.b<{ $total?: boolean }>`
  /* A figure is one line. Squeezed by a long label on a phone this broke
     mid-number — "$120,00" over "0", and "12.5" over "%" — which reads as a
     different quantity, not as the same one wrapped. The label gives way
     instead; it is prose and can take another line. */
  flex: none;
  white-space: nowrap;
  font-size: ${({ $total }) => ($total ? "15px" : "13px")};
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${colors.gray[900]};
`;

export const DialogRows = styled(DialogPanel)`
  display: flex;
  flex-direction: column;
  padding: 4px 14px;
`;

export interface DialogRowProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** The sum of the lines above it. */
  total?: boolean;
}

export const DialogRow: React.FC<DialogRowProps> = ({
  label,
  value,
  total,
}) => (
  <RowLine $total={total}>
    <span>{label}</span>
    <RowValue $total={total}>{value}</RowValue>
  </RowLine>
);

/** A standing note at the foot of a dialog body, ruled off from the controls. */
export const DialogNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-top: ${space.md}px;
  border-top: 1px solid ${line.hairline};
  color: ${colors.gray[600]};
  font-size: 12px;
  line-height: 1.5;
`;

export const DialogNoteMark = styled.span`
  display: flex;
  flex: none;
  margin-top: 1px;
  color: ${colors.primary[500]};
`;

/** The help control that opens a note's caveats. */
export const DialogNoteHelp = styled.span`
  display: inline-flex;
  vertical-align: -3px;
  margin-left: 5px;
  color: ${colors.gray[400]};
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.gray[700]};
  }
`;

/** The body's own stack: sections with air between them. */
export const DialogBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space.lg}px;
`;

/** A labelled row of controls — a caption on the left, the control on the right. */
export const DialogField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space.md}px;
`;

export const DialogFieldLabel = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: ${colors.gray[700]};
`;

/** Two controls side by side, stacking when there is no room. */
export const DialogGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${space.md}px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

/** What a field means, under the field. */
export const DialogHint = styled.p`
  margin: 5px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: ${colors.gray[500]};
`;
